import { useAtom } from "jotai";
import { useState, useEffect } from "react";
import {
  baseShippingCategoryAtom,
  regionRegion,
} from "lib/core-react/store/store";
import React from "react";
import { Alert, Button, Form, Input, Select, TreeSelect } from "antd";
import { renderOptionsFromEnum } from "components/Form/forms";
import { FormInstance } from "antd/es/form/Form";
import {
  ShippingCategoryDataModel,
  ShippingCategoryRequestModel,
} from "models/shippingCategory";
import {
  useGetBaseShippingCategory,
  useGetRegion,
  useUpdateShippingCategory,
} from "lib/core-react/hooks/private";
import { ShippingCategoryDataStatus } from "enums/shippingCategoryCollectionEnums";
import { BaseShippingCategoryModel } from "models/baseShippingCategory";
import { RegionModel } from "models/region";

interface IProps {
  data: ShippingCategoryDataModel;
  setIsShowUpdateModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const UpdateShippingCategory = ({ data, setIsShowUpdateModal }: IProps) => {
  const { updateShippingCategory, isLoading, isError } =
    useUpdateShippingCategory();
  const [isErrorVisible, setIsErorrVisible] = useState(false);

  const formRef: React.Ref<FormInstance<any>> = React.createRef();
  const [form] = Form.useForm();

  useEffect(() => {
    return () => {
      form.resetFields();
    };
  }, [data, form]);

  //hide error message after 5 sec
  useEffect(() => {
    if (isErrorVisible) {
      setTimeout(() => {
        setIsErorrVisible(false);
      }, 5000);
    }
  }, [isErrorVisible]);

  useEffect(() => {
    getRegion("per_page=200");
    getBaseShippingCategory("per_page=200");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // region, base Shipping category
  const { getRegion } = useGetRegion();
  const { getBaseShippingCategory } = useGetBaseShippingCategory();

  const [
    {
      data: baseShippingCategoryDataAtom,
      isLoading: baseShippingCategoryLoading,
    },
  ] = useAtom(baseShippingCategoryAtom);

  const [{ data: regionDataAtom, isLoading: regionLoading }] =
    useAtom(regionRegion);

  const baseShippingCategoryTreeId =
    baseShippingCategoryDataAtom &&
    new BaseShippingCategoryModel(
      baseShippingCategoryDataAtom,
    ).getBaseShippingCategoryTreeIds();
  const regionDataTreeId =
    regionDataAtom && new RegionModel(regionDataAtom).getRegionIdsAsObject();

  const onFinish = async (value: ShippingCategoryRequestModel) => {
    const payload = {
      name: value.name,
      status: value.status,
      slug: value.slug,
      region_id: value.region_id,
      base_shipping_category_id: value.base_shipping_category_id,
    };

    try {
      await updateShippingCategory(
        data.id as string,
        payload as ShippingCategoryRequestModel,
      );
      setIsShowUpdateModal(false);
      setIsErorrVisible(false);
    } catch (error) {
      setIsErorrVisible(true);
    }
  };

  const onValuesChange = (changedValues: any, allValues: any) => {
    if (changedValues.name) {
      const slug = changedValues.name.toLowerCase().replace(/\s+/g, "-");
      form.setFieldsValue({ slug });
    }
  };

  return (
    <Form
      onFinish={onFinish}
      form={form}
      layout="vertical"
      name="Update_Shipping_Category"
      ref={formRef}
      initialValues={{
        slug: data.slug,
        name: data.name,
        parent_id: data.parent_id,
        status: data.status,
      }}
      onValuesChange={onValuesChange}
    >
      <Form.Item
        rules={[
          {
            required: true,
            message: "Shipping category name is required",
          },
        ]}
        label="Name"
        name="name"
      >
        <Input placeholder="Shipping category name" />
      </Form.Item>

      <Form.Item
        rules={[{ required: true, message: "Slug is required" }]}
        label="Slug"
        name="slug"
      >
        <Input placeholder="Slug name" />
      </Form.Item>
      <Form.Item
        label="Select Base Shipping Category"
        name="base_shipping_category_id"
        rules={[
          { required: true, message: "Base Shipping Category is required" },
        ]}
      >
        <TreeSelect
          showSearch
          dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
          placeholder="Please select a base shipping category"
          allowClear
          loading={baseShippingCategoryLoading}
          treeLine={{ showLeafIcon: true }}
          treeData={baseShippingCategoryTreeId}
        />
      </Form.Item>

      <Form.Item
        rules={[{ required: true, message: "Region is required" }]}
        name="region_id"
        label="Region"
      >
        <Select loading={regionLoading} placeholder="Please select a region">
          {regionDataTreeId && renderOptionsFromEnum(regionDataTreeId)}
        </Select>
      </Form.Item>
      <Form.Item
        rules={[{ required: true, message: "Status is required" }]}
        name="status"
        label="Status"
      >
        <Select placeholder="Please select a status">
          {renderOptionsFromEnum(ShippingCategoryDataStatus)}
        </Select>
      </Form.Item>

      <Form.Item>
        <Button
          loading={isLoading}
          type="primary"
          htmlType="submit"
          style={{ width: "100%" }}
        >
          Submit
        </Button>
      </Form.Item>

      {isErrorVisible && (
        <Alert
          style={{ marginTop: "7px" }}
          message={isError}
          closable
          afterClose={() => setIsErorrVisible(false)}
          type="error"
          showIcon
        />
      )}
    </Form>
  );
};

export default UpdateShippingCategory;
